import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PrintableDocument from '@/components/PrintableDocument';
import { api } from '@/api';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { isProfessionalRole } from '@/utils/roles';
import CorrespondenceTab from '@/components/correspondence/CorrespondenceTab';
import SendToTaskGenerator from '@/components/correspondence/SendToTaskGenerator';
import DocumentGeneratorButton from '@/components/DocumentGeneratorButton';
import { 
  ArrowLeft, FileText, Upload, Send, MessageSquare, Clock, Calendar,
  AlertTriangle, CheckCircle, XCircle, Paperclip, Download, Trash2,
  ArrowRight, RefreshCw, Loader2, User, Building2, Mail, Edit2,
  FileQuestion, AlertCircle, Link as LinkIcon, Bell
} from 'lucide-react';

const SI_COMMENT_TAGS = ['Time', 'Cost', 'Scope', 'Quality', 'Risk', 'Commercial', 'Information Needed', 'Other'];

const getStatusColor = (status, isVoided = false) => {
  if (isVoided) return 'bg-slate-200 text-slate-500 border-slate-300 line-through';
  
  switch (status) {
    case 'Draft': return 'bg-slate-100 text-slate-700 border-slate-200';
    case 'Captured': return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'Pending Approval': return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'Approved': return 'bg-green-100 text-green-700 border-green-200';
    case 'Issued': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'Distributed for Internal Comment': return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'Under Consultant Clarification': return 'bg-purple-100 text-purple-700 border-purple-200';
    case 'Closed': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'Voided': return 'bg-red-100 text-red-700 border-red-200 line-through';
    case 'Converted to RFI': return 'bg-cyan-100 text-cyan-700 border-cyan-200';
    case 'Converted to Notice': return 'bg-orange-100 text-orange-700 border-orange-200';
    default: return 'bg-slate-100 text-slate-700 border-slate-200';
  }
};

export default function SIDetail() {
  const { projectId, siId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  
  const [si, setSi] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Sub-data
  const [correspondence, setCorrespondence] = useState([]);
  const [internalComments, setInternalComments] = useState([]);
  const [clarifications, setClarifications] = useState([]);
  
  // Dialogs
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [clarificationDialogOpen, setClarificationDialogOpen] = useState(false);
  const [convertRfiDialogOpen, setConvertRfiDialogOpen] = useState(false);
  const [convertNoticeDialogOpen, setConvertNoticeDialogOpen] = useState(false);
  const [convertDelayEventDialogOpen, setConvertDelayEventDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [distributeDialogOpen, setDistributeDialogOpen] = useState(false);
  const [consolidationDialogOpen, setConsolidationDialogOpen] = useState(false);
  
  // Form state
  const [uploading, setUploading] = useState(false);
  const [uploadTag, setUploadTag] = useState('Scan');
  const [newComment, setNewComment] = useState({ text: '', tags: [], visibility: 'Internal only' });
  const [submittingComment, setSubmittingComment] = useState(false);
  const [newClarification, setNewClarification] = useState('');
  const [submittingClarification, setSubmittingClarification] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Distribution state
  const [internalTeam, setInternalTeam] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [distributing, setDistributing] = useState(false);
  const [distributionTokens, setDistributionTokens] = useState([]);
  
  // Consolidation state
  const [consolidation, setConsolidation] = useState(null);
  const [consolidating, setConsolidating] = useState(false);
  
  // Clarification state
  const [clarificationQuestions, setClarificationQuestions] = useState('');
  const [sendingClarification, setSendingClarification] = useState(false);
  const [clarificationToken, setClarificationToken] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(false);
  
  const isAdmin = user?.role === 'admin';
  const isConsultant = isProfessionalRole(user?.role) || user?.role === 'admin' || user?.role === 'contracts_manager';
  const isCreator = si?.created_by_user_id === user?.id;
  const canDeleteSI = isCreator && si?.status === 'Draft' && !si?.is_voided;
  const canVoidSI = isConsultant && !si?.is_voided && si?.source === 'Consultant Portal';
  const canSubmitForApproval = isCreator && si?.status === 'Draft' && !si?.is_voided;
  const canApprove = isConsultant && si?.status === 'Pending Approval';
  
  // Workflow dialogs
  const [submitApprovalDialogOpen, setSubmitApprovalDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  
  // Workflow form state
  const [selectedConsultant, setSelectedConsultant] = useState('');
  const [approvalComments, setApprovalComments] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [voidReason, setVoidReason] = useState('');
  const [submittingWorkflow, setSubmittingWorkflow] = useState(false);
  const [consultants, setConsultants] = useState([]);

  useEffect(() => {
    loadData();
  }, [siId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [siRes, projectRes] = await Promise.all([
        api.siteInstructions.getById(siId),
        api.projects.getById(projectId)
      ]);
      setSi(siRes.data);
      setProject(projectRes.data.project);
      
      // Load sub-data - these may fail if no data exists yet
      const [corrRes, commentsRes, clarRes] = await Promise.all([
        api.siteInstructions.getCorrespondence(siId).catch(() => ({ data: [] })),
        api.siteInstructions.getInternalComments(siId).catch(() => ({ data: [] })),
        api.siteInstructions.getClarifications(siId).catch(() => ({ data: [] }))
      ]);
      setCorrespondence(corrRes.data || []);
      setInternalComments(commentsRes.data || []);
      setClarifications(clarRes.data || []);
    } catch (error) {
      toast.error('Failed to load Site Instruction');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.siteInstructions.uploadAttachment(siId, formData, uploadTag);
      toast.success('Attachment uploaded');
      setUploadDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to upload attachment');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await api.siteInstructions.deleteAttachment(siId, attachmentId);
      toast.success('Attachment deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.text.trim()) {
      toast.error('Comment text is required');
      return;
    }
    
    setSubmittingComment(true);
    try {
      await api.siteInstructions.addInternalComment(siId, {
        comment_text: newComment.text,
        comment_tags: newComment.tags,
        visibility: newComment.visibility
      });
      toast.success('Comment added');
      setCommentDialogOpen(false);
      setNewComment({ text: '', tags: [], visibility: 'Internal only' });
      loadData();
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleAddClarification = async () => {
    if (!newClarification.trim()) {
      toast.error('Clarification text is required');
      return;
    }
    
    setSubmittingClarification(true);
    try {
      await api.siteInstructions.addClarification(siId, {
        clarification_text: newClarification
      });
      toast.success('Clarification added');
      setClarificationDialogOpen(false);
      setNewClarification('');
      loadData();
    } catch (error) {
      toast.error('Failed to add clarification');
    } finally {
      setSubmittingClarification(false);
    }
  };

  const handleClose = async () => {
    try {
      await api.siteInstructions.close(siId, 'Closed by user');
      toast.success('Site Instruction closed');
      loadData();
    } catch (error) {
      toast.error('Failed to close SI');
    }
  };

  const handleConvertToRfi = async () => {
    try {
      const result = await api.siteInstructions.convertToRfi(siId, {
        title: `RFI from SI ${si.si_number}`,
        query: si.si_description || si.si_title
      });
      toast.success(`Converted to ${result.data.rfi_number}`);
      setConvertRfiDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to convert to RFI');
    }
  };

  const handleConvertToNotice = async () => {
    try {
      const result = await api.siteInstructions.convertToNotice(siId, {
        event_type: 'Site Instruction',
        narrative: `Delay arising from SI ${si.si_number}: ${si.si_description || si.si_title}`
      });
      toast.success('Converted to Delay Event');
      setConvertNoticeDialogOpen(false);
      // Redirect to the newly created delay event
      if (result.data?.event_id) {
        navigate(`/events/${result.data.event_id}`);
      } else {
        loadData();
      }
    } catch (error) {
      toast.error('Failed to convert to Notice');
    }
  };

  const handleConvertToDelayEvent = async () => {
    try {
      const result = await api.siteInstructions.convertToDelayEvent(siId, {
        event_type: 'Site Instruction',
        description: `Delay arising from SI ${si.si_number}: ${si.si_description || si.si_title}`
      });
      toast.success('Converted to Delay Event');
      setConvertDelayEventDialogOpen(false);
      // Redirect to the newly created delay event
      if (result.data?.event_id) {
        navigate(`/events/${result.data.event_id}`);
      } else {
        loadData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert to Delay Event');
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.siteInstructions.delete(siId);
      toast.success('Site Instruction deleted');
      navigate(`/projects/${projectId}#si`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete SI');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  // Load consultants for approval submission
  const loadConsultants = async () => {
    try {
      const response = await api.professionalTeam.getAll(projectId);
      setConsultants(response.data || []);
    } catch (error) {
      console.error('Failed to load consultants:', error);
      setConsultants([]);
    }
  };

  // Handle Submit for Approval
  const handleSubmitForApproval = async () => {
    if (!selectedConsultant) {
      toast.error('Please select a consultant for approval');
      return;
    }

    setSubmittingWorkflow(true);
    try {
      await api.siteInstructions.submitForApproval(siId, { approval_consultant_id: selectedConsultant });
      toast.success('SI submitted for consultant approval');
      setSubmitApprovalDialogOpen(false);
      setSelectedConsultant('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit for approval');
    } finally {
      setSubmittingWorkflow(false);
    }
  };

  // Handle Approve SI
  const handleApprove = async () => {
    setSubmittingWorkflow(true);
    try {
      await api.siteInstructions.approve(siId, { comments: approvalComments });
      toast.success('Site Instruction approved');
      setApproveDialogOpen(false);
      setApprovalComments('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve SI');
    } finally {
      setSubmittingWorkflow(false);
    }
  };

  // Handle Reject SI
  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setSubmittingWorkflow(true);
    try {
      await api.siteInstructions.reject(siId, { comments: rejectReason });
      toast.success('SI rejected and returned to contractor');
      setRejectDialogOpen(false);
      setRejectReason('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject SI');
    } finally {
      setSubmittingWorkflow(false);
    }
  };

  // Handle Void SI
  const handleVoid = async () => {
    if (!voidReason.trim()) {
      toast.error('Please provide a reason for voiding');
      return;
    }

    setSubmittingWorkflow(true);
    try {
      await api.siteInstructions.void(siId, { reason: voidReason });
      toast.success('Site Instruction voided');
      setVoidDialogOpen(false);
      setVoidReason('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to void SI');
    } finally {
      setSubmittingWorkflow(false);
    }
  };

  // Load internal team when distribute dialog opens
  const loadInternalTeam = async () => {
    try {
      const response = await api.internalTeam.getAll(projectId);
      setInternalTeam(response.data || []);
    } catch (error) {
      console.error('Failed to load internal team:', error);
      setInternalTeam([]);
    }
  };

  // Toggle recipient selection
  const toggleRecipient = (member) => {
    const isSelected = selectedRecipients.some(r => r.id === member.id);
    if (isSelected) {
      setSelectedRecipients(selectedRecipients.filter(r => r.id !== member.id));
    } else {
      setSelectedRecipients([...selectedRecipients, {
        id: member.id,
        name: `${member.name} ${member.surname || ''}`.trim(),
        email: member.email
      }]);
    }
  };

  // Handle distribute
  const handleDistribute = async () => {
    if (selectedRecipients.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    setDistributing(true);
    try {
      const response = await api.siteInstructions.distributeInternal(siId, selectedRecipients);
      setDistributionTokens(response.data.tokens);
      toast.success(`Distributed to ${selectedRecipients.length} team members`);
      loadData();
      // Don't close dialog yet - show the generated links
    } catch (error) {
      toast.error('Failed to distribute SI');
    } finally {
      setDistributing(false);
    }
  };

  // Generate comment link URL
  const getCommentLink = (token) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/public/si-comment/${token.id}`;
  };

  // Copy link to clipboard
  const copyLink = (token) => {
    const link = getCommentLink(token);
    navigator.clipboard.writeText(link);
    toast.success(`Link copied for ${token.recipient_name}`);
  };

  // Handle consolidation
  const handleConsolidate = async () => {
    setConsolidating(true);
    try {
      const response = await api.siteInstructions.consolidateComments(siId);
      setConsolidation(response.data);
      toast.success('Comments consolidated successfully');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('No comments to consolidate');
      } else {
        toast.error('Failed to consolidate comments');
      }
    } finally {
      setConsolidating(false);
    }
  };

  // Load existing consolidation
  const loadConsolidation = async () => {
    try {
      const response = await api.siteInstructions.getConsolidation(siId);
      setConsolidation(response.data);
    } catch (error) {
      // No consolidation exists yet
      setConsolidation(null);
    }
  };

  // Handle send for clarification
  const handleSendForClarification = async () => {
    if (!clarificationQuestions.trim()) {
      toast.error('Please enter the questions you need clarified');
      return;
    }

    // Check if consultant is assigned
    if (!si.issued_by_consultant_id) {
      toast.error('No consultant assigned to this SI. Please assign a consultant first.');
      return;
    }

    setSendingClarification(true);
    try {
      const response = await api.siteInstructions.sendForClarification(siId, clarificationQuestions);
      setClarificationToken(response.data.token);
      toast.success('Clarification request sent to consultant');
      loadData();
    } catch (error) {
      console.error('Send clarification error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send clarification request');
    } finally {
      setSendingClarification(false);
    }
  };

  // Get clarification link URL
  const getClarificationLink = (token) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/public/si-clarification/${token.id}`;
  };

  // Copy clarification link
  const copyClarificationLink = (token) => {
    const link = getClarificationLink(token);
    navigator.clipboard.writeText(link);
    toast.success('Clarification link copied to clipboard');
  };

  // Send reminder to consultant
  const handleSendReminder = async () => {
    setSendingReminder(true);
    try {
      const response = await api.siteInstructions.sendReminder(siId);
      if (response.data.success) {
        toast.success(response.data.message || 'Reminder sent successfully');
      } else {
        toast.error(response.data.error || 'Failed to send reminder');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminder(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  if (!si) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <h2 className="text-xl font-bold text-slate-500">Site Instruction not found</h2>
        </div>
      </DashboardLayout>
    );
  }

  const isOverdue = () => {
    const dueDate = si.due_date_for_clarification ? new Date(si.due_date_for_clarification) : null;
    return dueDate && dueDate < new Date() && 
      ['Distributed for Internal Comment', 'Under Consultant Clarification'].includes(si.status);
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto overflow-x-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          {/* Header Section - Mobile Responsive */}
          <div className="flex flex-col gap-3">
            {/* Back button + Title row */}
            <div className="flex items-start gap-2 sm:gap-4">
              <Button
                onClick={() => navigate(`/projects/${projectId}#si`)}
                variant="ghost"
                size="sm"
                className="text-slate-600 shrink-0 -ml-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline ml-1">Back</span>
              </Button>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className={`text-lg sm:text-2xl md:text-3xl font-barlow font-bold uppercase font-mono ${si.is_voided ? 'text-slate-400 line-through' : ''}`}>{si.si_number}</h1>
                  <Badge className={`${getStatusColor(si.status, si.is_voided)} text-xs`}>{si.status.split(' ').slice(0, 2).join(' ')}</Badge>
                  {si.is_voided && (
                    <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">
                      <XCircle className="h-3 w-3 mr-1" />
                      VOID
                    </Badge>
                  )}
                  {isOverdue() && !si.is_voided && (
                    <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Overdue
                    </Badge>
                  )}
                </div>
                <p className="text-xs sm:text-sm text-slate-500 mt-1 truncate">{project?.name}</p>
              </div>
            </div>
            
            {/* SI Title - separate row for mobile */}
            <h2 className="text-sm sm:text-base md:text-xl text-slate-700 line-clamp-2">{si.si_title}</h2>
            
            {/* Source/Linked Info */}
            {si.source_type && si.source_id && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 uppercase">Created from:</span>
                <button
                  onClick={() => {
                    if (si.source_type === 'Shop Drawing') {
                      navigate(`/projects/${projectId}/drawings/${si.source_id}`);
                    }
                  }}
                  className="inline-flex items-center gap-1 text-xs sm:text-sm text-blue-600 hover:text-blue-800 hover:underline"
                  data-testid="si-source-link"
                >
                  <FileText className="h-3 w-3" />
                  {si.source_reference || si.source_type}
                  <ArrowRight className="h-3 w-3" />
                </button>
              </div>
            )}
            {si.linked_delay_event_id && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 uppercase">Linked to:</span>
                <button
                  onClick={() => navigate(`/events/${si.linked_delay_event_id}`)}
                  className="inline-flex items-center gap-1 text-xs sm:text-sm text-amber-600 hover:text-amber-800 hover:underline"
                  data-testid="linked-delay-event"
                >
                  <AlertTriangle className="h-3 w-3" />
                  Delay Event
                  <ArrowRight className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>
          
          {/* Action Buttons - Scrollable on mobile */}
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:pb-0 sm:flex-wrap">
            <SendToTaskGenerator
              projectId={projectId}
              moduleType="SI"
              moduleEntityId={siId}
              moduleReference={si.si_number}
            />
            <DocumentGeneratorButton
              documentType="SI"
              projectId={projectId}
              entityId={siId}
              entityData={si}
              projectData={project}
            />
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-sm">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="text-xl font-barlow font-bold uppercase">Upload Attachment</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Tag</Label>
                    <Select value={uploadTag} onValueChange={setUploadTag}>
                      <SelectTrigger className="rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Scan">Scan</SelectItem>
                        <SelectItem value="Photo">Photo</SelectItem>
                        <SelectItem value="Drawing">Drawing</SelectItem>
                        <SelectItem value="Email">Email</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileUpload}
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
                  >
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                    Select File
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            
            {si.status !== 'Closed' && !si.status.startsWith('Converted') && !si.is_voided && (
              <>
                {/* WORKFLOW BUTTONS */}
                
                {/* Submit for Approval - Only for Draft SIs created by contractor */}
                {canSubmitForApproval && (
                  <Dialog open={submitApprovalDialogOpen} onOpenChange={(open) => {
                    setSubmitApprovalDialogOpen(open);
                    if (open) loadConsultants();
                  }}>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        className="rounded-sm border-blue-500 text-blue-600 hover:bg-blue-50"
                        data-testid="submit-for-approval-btn"
                      >
                        <Send className="h-4 w-4 mr-2" />
                        Submit for Approval
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle className="text-xl font-barlow font-bold uppercase">Submit for Consultant Approval</DialogTitle>
                        <DialogDescription>Select a consultant to review and approve this Site Instruction.</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Select Consultant *</Label>
                          <Select value={selectedConsultant} onValueChange={setSelectedConsultant}>
                            <SelectTrigger className="rounded-sm" data-testid="approval-consultant-select">
                              <SelectValue placeholder="Select consultant..." />
                            </SelectTrigger>
                            <SelectContent>
                              {consultants.map(c => (
                                <SelectItem key={c.id} value={c.id}>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{c.company_name || c.name}</span>
                                    {c.discipline && (
                                      <span className="text-xs text-slate-500">({c.discipline})</span>
                                    )}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {consultants.length === 0 && (
                          <p className="text-sm text-amber-600">
                            No consultants found. Add consultants to the Professional Team first.
                          </p>
                        )}
                        <div className="flex gap-2">
                          <Button variant="outline" onClick={() => setSubmitApprovalDialogOpen(false)} className="flex-1">
                            Cancel
                          </Button>
                          <Button
                            onClick={handleSubmitForApproval}
                            disabled={submittingWorkflow || !selectedConsultant}
                            className="flex-1 bg-blue-600 text-white hover:bg-blue-700"
                            data-testid="confirm-submit-approval-btn"
                          >
                            {submittingWorkflow ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Submit for Approval
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
                
                {/* Approve/Reject - Only for consultants when SI is Pending Approval */}
                {canApprove && (
                  <>
                    <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
                      <DialogTrigger asChild>
                        <Button
                          className="rounded-sm bg-emerald-600 text-white hover:bg-emerald-700"
                          data-testid="approve-si-btn"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Approve
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="text-xl font-barlow font-bold uppercase text-emerald-700">Approve Site Instruction</DialogTitle>
                          <DialogDescription>Approve this SI to make it official and notify the contractor.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Comments (Optional)</Label>
                            <Textarea
                              value={approvalComments}
                              onChange={(e) => setApprovalComments(e.target.value)}
                              placeholder="Add any approval comments..."
                              rows={3}
                              className="rounded-sm"
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setApproveDialogOpen(false)} className="flex-1">
                              Cancel
                            </Button>
                            <Button
                              onClick={handleApprove}
                              disabled={submittingWorkflow}
                              className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700"
                              data-testid="confirm-approve-btn"
                            >
                              {submittingWorkflow ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                              Approve SI
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                    
                    <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          className="rounded-sm border-red-500 text-red-600 hover:bg-red-50"
                          data-testid="reject-si-btn"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Reject
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="text-xl font-barlow font-bold uppercase text-red-600">Reject Site Instruction</DialogTitle>
                          <DialogDescription>Reject this SI and return it to the contractor for corrections.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Reason for Rejection *</Label>
                            <Textarea
                              value={rejectReason}
                              onChange={(e) => setRejectReason(e.target.value)}
                              placeholder="Please explain why this SI is being rejected..."
                              rows={4}
                              className="rounded-sm"
                              data-testid="reject-reason-input"
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setRejectDialogOpen(false)} className="flex-1">
                              Cancel
                            </Button>
                            <Button
                              onClick={handleReject}
                              disabled={submittingWorkflow || !rejectReason.trim()}
                              className="flex-1 bg-red-600 text-white hover:bg-red-700"
                              data-testid="confirm-reject-btn"
                            >
                              {submittingWorkflow ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                              Reject SI
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </>
                )}
                
                {/* Void - Only for consultants who created the SI */}
                {canVoidSI && (
                  <Dialog open={voidDialogOpen} onOpenChange={setVoidDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        className="rounded-sm border-slate-500 text-slate-600 hover:bg-slate-100"
                        data-testid="void-si-btn"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Void SI
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle className="text-xl font-barlow font-bold uppercase text-red-600">Void Site Instruction</DialogTitle>
                        <DialogDescription>Mark this SI as void. It will remain visible but greyed out.</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                          <p className="text-sm text-amber-800">
                            <AlertTriangle className="h-4 w-4 inline mr-1" />
                            Voiding an SI does not delete it. The SI will remain visible but marked as void.
                          </p>
                        </div>
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Reason for Voiding *</Label>
                          <Textarea
                            value={voidReason}
                            onChange={(e) => setVoidReason(e.target.value)}
                            placeholder="Please explain why this SI is being voided..."
                            rows={4}
                            className="rounded-sm"
                            data-testid="void-reason-input"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" onClick={() => setVoidDialogOpen(false)} className="flex-1">
                            Cancel
                          </Button>
                          <Button
                            onClick={handleVoid}
                            disabled={submittingWorkflow || !voidReason.trim()}
                            className="flex-1 bg-slate-700 text-white hover:bg-slate-800"
                            data-testid="confirm-void-btn"
                          >
                            {submittingWorkflow ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Void SI
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
                
                {/* Distribute for Internal Comment */}
                <Dialog open={distributeDialogOpen} onOpenChange={(open) => {
                  setDistributeDialogOpen(open);
                  if (open) {
                    loadInternalTeam();
                    setSelectedRecipients([]);
                    setDistributionTokens([]);
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="rounded-sm border-amber-500 text-amber-600 hover:bg-amber-50"
                      data-testid="distribute-si-button"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Distribute for Comment
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl" aria-describedby="distribute-dialog-desc">
                    <DialogHeader>
                      <DialogTitle className="text-xl font-barlow font-bold uppercase">
                        Distribute for Internal Comment
                      </DialogTitle>
                      <DialogDescription id="distribute-dialog-desc">
                        Send secure comment links to internal team members
                      </DialogDescription>
                    </DialogHeader>
                    
                    {distributionTokens.length > 0 ? (
                      <div className="space-y-4">
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="h-5 w-5 text-emerald-600" />
                            <span className="font-semibold text-emerald-800">
                              Links Generated Successfully!
                            </span>
                          </div>
                          <p className="text-sm text-emerald-700">
                            Share these secure links with team members. Each link expires in 14 days.
                          </p>
                        </div>
                        
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {distributionTokens.map(token => (
                            <div 
                              key={token.id} 
                              className="flex items-center justify-between bg-slate-50 rounded-lg p-3"
                            >
                              <div>
                                <p className="font-medium text-slate-900">{token.recipient_name}</p>
                                <p className="text-xs text-slate-500">{token.recipient_email}</p>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => copyLink(token)}
                                className="rounded-sm"
                              >
                                Copy Link
                              </Button>
                            </div>
                          ))}
                        </div>
                        
                        <Button
                          onClick={() => setDistributeDialogOpen(false)}
                          className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
                        >
                          Done
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                            Select Team Members
                          </Label>
                          {internalTeam.length === 0 ? (
                            <p className="text-sm text-amber-600">
                              No internal team members found. Add team members in the Internal Team tab first.
                            </p>
                          ) : (
                            <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg">
                              {internalTeam.map(member => {
                                const isSelected = selectedRecipients.some(r => r.id === member.id);
                                return (
                                  <div
                                    key={member.id}
                                    onClick={() => toggleRecipient(member)}
                                    className={`flex items-center gap-3 p-3 border-b border-slate-100 last:border-b-0 cursor-pointer transition-colors ${
                                      isSelected ? 'bg-slate-100' : 'hover:bg-slate-50'
                                    }`}
                                  >
                                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                      isSelected ? 'bg-slate-900 border-slate-900' : 'border-slate-300'
                                    }`}>
                                      {isSelected && <CheckCircle className="h-3 w-3 text-white" />}
                                    </div>
                                    <div className="flex-1">
                                      <p className="font-medium text-slate-900">
                                        {member.name} {member.surname || ''}
                                      </p>
                                      <p className="text-xs text-slate-500">
                                        {member.position || member.role} • {member.email}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        
                        {selectedRecipients.length > 0 && (
                          <p className="text-sm text-slate-600">
                            {selectedRecipients.length} recipient(s) selected
                          </p>
                        )}
                        
                        <Button
                          onClick={handleDistribute}
                          disabled={distributing || selectedRecipients.length === 0}
                          className="w-full bg-amber-600 text-white hover:bg-amber-700 rounded-sm"
                          data-testid="send-distribution-button"
                        >
                          {distributing ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4 mr-2" />
                          )}
                          Generate Comment Links
                        </Button>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
                
                {/* Send for Clarification Dialog */}
                <Dialog open={clarificationDialogOpen} onOpenChange={(open) => {
                  setClarificationDialogOpen(open);
                  if (open) {
                    setClarificationQuestions('');
                    setClarificationToken(null);
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="rounded-sm border-cyan-500 text-cyan-600 hover:bg-cyan-50"
                      data-testid="clarify-si-button"
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Request Clarification
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-xl" aria-describedby="clarify-dialog-desc">
                    <DialogHeader>
                      <DialogTitle className="text-xl font-barlow font-bold uppercase">
                        Request Consultant Clarification
                      </DialogTitle>
                      <DialogDescription id="clarify-dialog-desc">
                        Send a secure link to the issuing consultant to request clarification
                      </DialogDescription>
                    </DialogHeader>
                    
                    {clarificationToken ? (
                      <div className="space-y-4">
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="h-5 w-5 text-emerald-600" />
                            <span className="font-semibold text-emerald-800">
                              Clarification Request Sent!
                            </span>
                          </div>
                          <p className="text-sm text-emerald-700">
                            A secure link has been generated for {clarificationToken.recipient_name}.
                            Response is due within 7 days.
                          </p>
                        </div>
                        
                        <div className="bg-slate-50 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-slate-900">{clarificationToken.recipient_name}</p>
                              <p className="text-xs text-slate-500">{clarificationToken.recipient_email}</p>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => copyClarificationLink(clarificationToken)}
                              className="rounded-sm"
                            >
                              Copy Link
                            </Button>
                          </div>
                        </div>
                        
                        <Button
                          onClick={() => setClarificationDialogOpen(false)}
                          className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
                        >
                          Done
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                          <p className="text-sm text-cyan-800">
                            <strong>Sending to:</strong> {si.issued_by_person_name || 'Issuing Consultant'}
                            {!si.issued_by_consultant_id && (
                              <span className="block text-amber-600 mt-1">
                                Warning: No consultant assigned to this SI
                              </span>
                            )}
                          </p>
                        </div>
                        
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                            Questions / Points Requiring Clarification
                          </Label>
                          <Textarea
                            value={clarificationQuestions}
                            onChange={(e) => setClarificationQuestions(e.target.value)}
                            placeholder="Please clarify the following points:&#10;&#10;1. ...&#10;2. ...&#10;3. ..."
                            rows={6}
                            className="rounded-sm"
                            data-testid="clarification-questions-input"
                          />
                        </div>
                        
                        <Button
                          onClick={handleSendForClarification}
                          disabled={sendingClarification || !clarificationQuestions.trim() || !si.issued_by_consultant_id}
                          className="w-full bg-cyan-600 text-white hover:bg-cyan-700 rounded-sm"
                          data-testid="send-clarification-button"
                        >
                          {sendingClarification ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4 mr-2" />
                          )}
                          Send Clarification Request
                        </Button>
                        
                        <p className="text-xs text-slate-400 text-center">
                          Consultant will have 7 days to respond
                        </p>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
                
                <Button
                  onClick={handleClose}
                  variant="outline"
                  className="rounded-sm border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Close SI
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 mb-6">
          <div className="flex gap-0 overflow-x-auto">
            {['overview', 'attachments', 'comments', 'clarifications', 'correspondence', 'linked'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 text-sm font-medium uppercase tracking-wide transition-colors whitespace-nowrap ${
                  activeTab === tab
                    ? 'border-b-2 border-slate-900 text-slate-900'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                {tab === 'overview' && <FileText className="h-4 w-4 inline mr-2" />}
                {tab === 'attachments' && <Paperclip className="h-4 w-4 inline mr-2" />}
                {tab === 'comments' && <MessageSquare className="h-4 w-4 inline mr-2" />}
                {tab === 'clarifications' && <Mail className="h-4 w-4 inline mr-2" />}
                {tab === 'correspondence' && <Clock className="h-4 w-4 inline mr-2" />}
                {tab === 'linked' && <LinkIcon className="h-4 w-4 inline mr-2" />}
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                {tab === 'comments' && internalComments.length > 0 && (
                  <Badge className="ml-2 bg-slate-100 text-slate-600">{internalComments.length}</Badge>
                )}
                {tab === 'attachments' && si.attachments?.length > 0 && (
                  <Badge className="ml-2 bg-slate-100 text-slate-600">{si.attachments.length}</Badge>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
            {/* Main Info */}
            <div className="xl:col-span-2 space-y-4 sm:space-y-6">
              <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Description</h3>
                <p className="text-slate-700 whitespace-pre-wrap">
                  {si.si_description || 'No description provided'}
                </p>
              </div>
              
              {si.drawings_referenced?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Drawings Referenced</h3>
                  <div className="space-y-2">
                    {si.drawings_referenced.map((d, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2 bg-slate-50 rounded-sm">
                        <FileText className="h-4 w-4 text-slate-400" />
                        <span className="font-mono">{d.drawing_number}</span>
                        {d.revision && <Badge className="bg-slate-100 text-xs">Rev {d.revision}</Badge>}
                        {d.description && <span className="text-sm text-slate-500">{d.description}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Action Buttons */}
              {si.status !== 'Closed' && !si.status.startsWith('Converted') && (
                <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Actions</h3>
                  <div className="flex flex-wrap gap-3">
                    <Dialog open={commentDialogOpen} onOpenChange={setCommentDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="rounded-sm">
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Add Comment
                        </Button>
                      </DialogTrigger>
                      <DialogContent aria-describedby="comment-dialog-description">
                        <DialogHeader>
                          <DialogTitle className="text-xl font-barlow font-bold uppercase">Add Internal Comment</DialogTitle>
                          <DialogDescription id="comment-dialog-description" className="sr-only">
                            Add an internal comment to this Site Instruction for team collaboration
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Comment</Label>
                            <Textarea
                              value={newComment.text}
                              onChange={(e) => setNewComment({ ...newComment, text: e.target.value })}
                              placeholder="Enter your comment..."
                              rows={4}
                              className="rounded-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Tags</Label>
                            <div className="flex flex-wrap gap-2">
                              {SI_COMMENT_TAGS.map(tag => (
                                <Badge
                                  key={tag}
                                  className={`cursor-pointer ${
                                    newComment.tags.includes(tag)
                                      ? 'bg-slate-900 text-white'
                                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                  }`}
                                  onClick={() => {
                                    if (newComment.tags.includes(tag)) {
                                      setNewComment({ ...newComment, tags: newComment.tags.filter(t => t !== tag) });
                                    } else {
                                      setNewComment({ ...newComment, tags: [...newComment.tags, tag] });
                                    }
                                  }}
                                >
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <Button
                            onClick={handleAddComment}
                            disabled={submittingComment}
                            className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
                          >
                            {submittingComment ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Add Comment
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                    
                    <Dialog open={convertRfiDialogOpen} onOpenChange={setConvertRfiDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="rounded-sm border-cyan-500 text-cyan-600 hover:bg-cyan-50">
                          <FileQuestion className="h-4 w-4 mr-2" />
                          Convert to RFI
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="text-xl font-barlow font-bold uppercase">Convert to RFI</DialogTitle>
                          <DialogDescription>
                            This will create an RFI based on this Site Instruction and link them together.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="bg-cyan-50 border border-cyan-200 rounded-sm p-4">
                            <p className="text-sm text-cyan-700">
                              SI {si.si_number} will be converted to an RFI. All attachments and timeline will be carried forward.
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setConvertRfiDialogOpen(false)} className="flex-1">
                              Cancel
                            </Button>
                            <Button onClick={handleConvertToRfi} className="flex-1 bg-cyan-600 text-white hover:bg-cyan-700">
                              Convert to RFI
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                    
                    <Dialog open={convertNoticeDialogOpen} onOpenChange={setConvertNoticeDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="rounded-sm border-orange-500 text-orange-600 hover:bg-orange-50">
                          <AlertCircle className="h-4 w-4 mr-2" />
                          Convert to Notice
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="text-xl font-barlow font-bold uppercase">Convert to Delay Notice</DialogTitle>
                          <DialogDescription>
                            This will create a Delay Event based on this Site Instruction.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="bg-orange-50 border border-orange-200 rounded-sm p-4">
                            <p className="text-sm text-orange-700">
                              SI {si.si_number} will be converted to a Delay Event. You can then proceed to create a formal Notice.
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setConvertNoticeDialogOpen(false)} className="flex-1">
                              Cancel
                            </Button>
                            <Button onClick={handleConvertToNotice} className="flex-1 bg-orange-600 text-white hover:bg-orange-700">
                              Convert to Notice
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                    
                    {/* Convert to Delay Event */}
                    <Dialog open={convertDelayEventDialogOpen} onOpenChange={setConvertDelayEventDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="rounded-sm border-amber-500 text-amber-600 hover:bg-amber-50" data-testid="convert-to-delay-event-btn">
                          <Clock className="h-4 w-4 mr-2" />
                          Convert to Delay Event
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="text-xl font-barlow font-bold uppercase">Convert to Delay Event</DialogTitle>
                          <DialogDescription>
                            Create a Delay Event linked to this Site Instruction.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
                            <p className="text-sm text-amber-700">
                              A new Delay Event will be created and linked to SI {si.si_number}. You'll be redirected to the event page where you can add details and track deadlines.
                            </p>
                          </div>
                          {si.linked_delay_event_id && (
                            <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
                              <p className="text-sm text-blue-700">
                                <strong>Note:</strong> This SI is already linked to a Delay Event.
                              </p>
                            </div>
                          )}
                          <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setConvertDelayEventDialogOpen(false)} className="flex-1">
                              Cancel
                            </Button>
                            <Button 
                              onClick={handleConvertToDelayEvent} 
                              className="flex-1 bg-amber-600 text-white hover:bg-amber-700"
                              data-testid="confirm-convert-delay-event-btn"
                            >
                              Create Delay Event
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              )}
            </div>
            
            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Details</h3>
                <div className="space-y-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Discipline</span>
                    <span className="font-medium">{si.discipline}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Issued By</span>
                    <span className="font-medium">{si.consultant?.name || 'Unknown'}</span>
                  </div>
                  {si.issued_by_person_name && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Person</span>
                      <span className="font-medium">{si.issued_by_person_name}</span>
                    </div>
                  )}
                  {si.issue_date && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Issue Date</span>
                      <span className="font-medium">{new Date(si.issue_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  {si.received_date && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Received</span>
                      <span className="font-medium">{new Date(si.received_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  {si.due_date_for_clarification && (
                    <div className={`flex justify-between ${isOverdue() ? 'text-red-600' : ''}`}>
                      <span className="text-slate-500">Due Date</span>
                      <span className="font-medium">{new Date(si.due_date_for_clarification).toLocaleDateString()}</span>
                    </div>
                  )}
                  {si.location_area && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Location</span>
                      <span className="font-medium">{si.location_area}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Impact Assessment</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Cost Impact</span>
                    <Badge className={
                      si.cost_impact_expected === 'Yes' ? 'bg-red-100 text-red-700' :
                      si.cost_impact_expected === 'No' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-slate-100 text-slate-600'
                    }>
                      {si.cost_impact_expected}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Time Impact</span>
                    <Badge className={
                      si.time_impact_expected === 'Yes' ? 'bg-red-100 text-red-700' :
                      si.time_impact_expected === 'No' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-slate-100 text-slate-600'
                    }>
                      {si.time_impact_expected}
                    </Badge>
                  </div>
                </div>
              </div>
              
              {/* Delete Section - Only for creator of Draft SIs or Admin */}
              {(canDeleteSI || isAdmin) && (
                <div className="bg-white border border-red-200 rounded-sm shadow-sm p-6">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-red-600 mb-2">Danger Zone</h3>
                  <p className="text-xs text-slate-500 mb-4">
                    {canDeleteSI ? 'You can delete this SI because it is still in Draft status.' : 'Admin: Permanently delete this SI'}
                  </p>
                  <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full border-red-500 text-red-600 hover:bg-red-50" data-testid="delete-si-btn">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete SI
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle className="text-xl font-barlow font-bold uppercase text-red-600">Delete Site Instruction</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <p className="text-sm text-slate-600">
                          Are you sure you want to delete SI <strong>{si.si_number}</strong>? This action cannot be undone.
                        </p>
                        {!canDeleteSI && !isAdmin && (
                          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                            <p className="text-sm text-amber-800">
                              <AlertTriangle className="h-4 w-4 inline mr-1" />
                              Only Draft SIs can be deleted. This SI is in "{si.status}" status.
                            </p>
                          </div>
                        )}
                        <div className="flex gap-2">
                          <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="flex-1">
                            Cancel
                          </Button>
                          <Button
                            onClick={handleDelete}
                            disabled={deleting}
                            className="flex-1 bg-red-600 text-white hover:bg-red-700"
                            data-testid="confirm-delete-btn"
                          >
                            {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Delete Permanently
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'attachments' && (
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-barlow font-bold uppercase">Attachments</h3>
              <Button onClick={() => setUploadDialogOpen(true)} variant="outline" className="rounded-sm">
                <Upload className="h-4 w-4 mr-2" />
                Upload
              </Button>
            </div>
            
            {(!si.attachments || si.attachments.length === 0) ? (
              <p className="text-slate-400 text-center py-8">No attachments yet</p>
            ) : (
              <div className="space-y-3">
                {si.attachments.map((att) => (
                  <div key={att.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-sm">
                    <div className="flex items-center gap-3">
                      <Paperclip className="h-5 w-5 text-slate-400" />
                      <div>
                        <p className="font-medium">{att.file_name}</p>
                        <div className="flex gap-2 text-xs text-slate-500">
                          <Badge className="bg-slate-100 text-slate-600">{att.tag}</Badge>
                          <span>{(att.file_size / 1024).toFixed(1)} KB</span>
                          <span>{new Date(att.uploaded_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" className="text-slate-600">
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={() => handleDeleteAttachment(att.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'comments' && (
          <div className="space-y-6">
            {/* AI Consolidation Card */}
            {internalComments.length >= 2 && (
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-sm p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-barlow font-bold text-purple-900 flex items-center gap-2">
                      <RefreshCw className="h-5 w-5" />
                      AI Comment Consolidation
                    </h3>
                    <p className="text-sm text-purple-700 mt-1">
                      {consolidation 
                        ? 'AI has consolidated all internal comments into a summary.'
                        : 'Use AI to consolidate all internal comments into a professional summary report.'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {consolidation ? (
                      <>
                        <Dialog open={consolidationDialogOpen} onOpenChange={setConsolidationDialogOpen}>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              className="rounded-sm border-purple-500 text-purple-700 hover:bg-purple-100"
                              onClick={loadConsolidation}
                            >
                              View Consolidation
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" aria-describedby="consolidation-desc">
                            <DialogHeader>
                              <DialogTitle className="text-xl font-barlow font-bold uppercase">
                                AI Consolidation Report
                              </DialogTitle>
                              <DialogDescription id="consolidation-desc">
                                AI-generated summary of all internal team comments
                              </DialogDescription>
                            </DialogHeader>
                            <div className="prose prose-sm max-w-none">
                              <div 
                                className="whitespace-pre-wrap text-slate-700"
                                dangerouslySetInnerHTML={{ 
                                  __html: consolidation?.consolidation_text?.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                    .replace(/^## (.*?)$/gm, '<h2 class="text-lg font-bold mt-4 mb-2">$1</h2>')
                                    .replace(/^### (.*?)$/gm, '<h3 class="text-base font-semibold mt-3 mb-1">$1</h3>')
                                    .replace(/^- (.*?)$/gm, '<li class="ml-4">$1</li>')
                                    .replace(/\n/g, '<br/>')
                                    || 'No consolidation available'
                                }}
                              />
                            </div>
                            {consolidation?.created_at && (
                              <p className="text-xs text-slate-400 mt-4">
                                Generated: {new Date(consolidation.created_at).toLocaleString()} 
                                ({consolidation.comments_included?.length || 0} comments)
                              </p>
                            )}
                          </DialogContent>
                        </Dialog>
                        <Button
                          onClick={handleConsolidate}
                          disabled={consolidating}
                          variant="outline"
                          className="rounded-sm"
                        >
                          {consolidating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Regenerate'}
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={handleConsolidate}
                        disabled={consolidating}
                        className="bg-purple-600 text-white hover:bg-purple-700 rounded-sm"
                        data-testid="consolidate-button"
                      >
                        {consolidating ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Consolidating...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Generate Consolidation
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Comments List */}
            <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-barlow font-bold uppercase">Internal Comments</h3>
                <Button onClick={() => setCommentDialogOpen(true)} variant="outline" className="rounded-sm">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Add Comment
                </Button>
              </div>
              
              {internalComments.length === 0 ? (
                <p className="text-slate-400 text-center py-8">No internal comments yet</p>
              ) : (
                <div className="space-y-4">
                  {internalComments.map((comment) => (
                    <div key={comment.id} className="border border-slate-200 rounded-sm p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-slate-400" />
                          <span className="font-medium">{comment.commenter_name}</span>
                          {comment.submitted_via === 'secure_link' && (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">via Link</Badge>
                          )}
                          {comment.commenter_role && (
                            <Badge className="bg-slate-100 text-slate-600 text-xs">{comment.commenter_role}</Badge>
                          )}
                        </div>
                        <span className="text-xs text-slate-400">{new Date(comment.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-slate-700 mb-2">{comment.comment_text}</p>
                      {/* Impact assessments */}
                      {(comment.cost_impact_assessment || comment.time_impact_assessment) && (
                        <div className="flex gap-3 mb-2 text-sm">
                          {comment.cost_impact_assessment && (
                            <span className={`${
                              comment.cost_impact_assessment === 'Yes' ? 'text-red-600' : 'text-slate-500'
                            }`}>
                              Cost: {comment.cost_impact_assessment}
                            </span>
                          )}
                          {comment.time_impact_assessment && (
                            <span className={`${
                              comment.time_impact_assessment === 'Yes' ? 'text-red-600' : 'text-slate-500'
                            }`}>
                              Time: {comment.time_impact_assessment}
                            </span>
                          )}
                        </div>
                      )}
                      {comment.recommended_action && (
                        <div className="bg-slate-50 rounded p-2 text-sm mb-2">
                          <span className="text-slate-500">Recommended: </span>
                          <span className="text-slate-700">{comment.recommended_action}</span>
                        </div>
                      )}
                      {comment.comment_tags?.length > 0 && (
                        <div className="flex gap-1">
                          {comment.comment_tags.map(tag => (
                            <Badge key={tag} className="bg-slate-100 text-slate-600 text-xs">{tag}</Badge>
                          ))}
                        </div>
                      )}
                      {comment.tags?.length > 0 && (
                        <div className="flex gap-1 mt-1">
                          {comment.tags.map(tag => (
                            <Badge key={tag} className="bg-slate-100 text-slate-600 text-xs">{tag}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'clarifications' && (
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-barlow font-bold uppercase">Consultant Clarifications</h3>
              <div className="flex gap-2">
                {si.status === 'Under Consultant Clarification' && (
                  <Button 
                    onClick={handleSendReminder} 
                    variant="outline" 
                    className="rounded-sm border-amber-300 text-amber-700 hover:bg-amber-50"
                    disabled={sendingReminder}
                    data-testid="send-reminder-btn"
                  >
                    {sendingReminder ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Bell className="h-4 w-4 mr-2" />
                    )}
                    Send Reminder
                  </Button>
                )}
                <Button onClick={() => setClarificationDialogOpen(true)} variant="outline" className="rounded-sm">
                  <Mail className="h-4 w-4 mr-2" />
                  Add Clarification
                </Button>
              </div>
            </div>
            
            {/* Add Clarification Dialog */}
            <Dialog open={clarificationDialogOpen} onOpenChange={setClarificationDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="text-xl font-barlow font-bold uppercase">Add Clarification</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Clarification</Label>
                    <Textarea
                      value={newClarification}
                      onChange={(e) => setNewClarification(e.target.value)}
                      placeholder="Enter clarification from consultant..."
                      rows={5}
                      className="rounded-sm"
                    />
                  </div>
                  <Button
                    onClick={handleAddClarification}
                    disabled={submittingClarification}
                    className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
                  >
                    {submittingClarification ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Add Clarification
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            
            {clarifications.length === 0 ? (
              <div className="text-center py-8">
                <Mail className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-400 mb-4">No clarifications received yet</p>
                {si.status !== 'Closed' && !si.status.startsWith('Converted') && (
                  <Button
                    variant="outline"
                    onClick={() => setClarificationDialogOpen(true)}
                    className="rounded-sm"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Request Clarification from Consultant
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {clarifications.map((clar) => (
                  <div key={clar.id} className="border border-cyan-200 bg-cyan-50/30 rounded-sm p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-cyan-600" />
                        <span className="font-medium text-cyan-800">{clar.consultant_name}</span>
                        {clar.company_name && (
                          <span className="text-xs text-slate-500">({clar.company_name})</span>
                        )}
                        {clar.submitted_via === 'secure_link' && (
                          <Badge className="bg-cyan-100 text-cyan-700 text-xs">via Secure Link</Badge>
                        )}
                      </div>
                      <span className="text-xs text-slate-400">
                        {new Date(clar.created_at || clar.submitted_at).toLocaleString()}
                      </span>
                    </div>
                    
                    {/* Questions Asked */}
                    {clar.questions_asked && (
                      <div className="bg-slate-100 rounded p-3 mb-3 text-sm">
                        <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Questions Asked:</p>
                        <p className="text-slate-600 whitespace-pre-wrap">{clar.questions_asked}</p>
                      </div>
                    )}
                    
                    {/* Response */}
                    <p className="text-slate-700 whitespace-pre-wrap">
                      {clar.response_text || clar.clarification_text}
                    </p>
                    
                    {/* Revised Drawings */}
                    {clar.revised_drawing_refs?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-cyan-200">
                        <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Revised Drawings:</p>
                        <div className="flex flex-wrap gap-1">
                          {clar.revised_drawing_refs.map((ref, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{ref}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Further Clarification Indicator */}
                    {clar.further_clarification_needed && (
                      <div className="mt-3 pt-3 border-t border-amber-200 bg-amber-50 -mx-4 -mb-4 p-4 rounded-b-sm">
                        <p className="text-sm font-medium text-amber-800 flex items-center gap-2">
                          <AlertCircle className="h-4 w-4" />
                          Consultant requires further information
                        </p>
                        {clar.further_questions && (
                          <p className="text-sm text-amber-700 mt-1">{clar.further_questions}</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'correspondence' && (
          <div className="space-y-6">
            {/* New Email Import System */}
            <CorrespondenceTab 
              projectId={projectId}
              moduleType="SI"
              moduleEntityId={siId}
              title="Email Correspondence"
            />
            
            {/* Legacy Correspondence Timeline */}
            <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6 mt-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-barlow font-bold uppercase">Manual Correspondence Timeline</h3>
                <Button onClick={loadData} variant="outline" size="sm" className="rounded-sm">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
              
              {correspondence.length === 0 ? (
                <p className="text-slate-400 text-center py-8">No correspondence yet</p>
              ) : (
                <div className="space-y-3">
                  {correspondence.map((entry) => (
                    <div key={entry.id} className="flex gap-4 p-4 bg-slate-50 rounded-sm">
                      <div className={`w-2 h-2 rounded-full mt-2 ${
                        entry.direction === 'Outgoing' ? 'bg-blue-500' :
                        entry.direction === 'Incoming' ? 'bg-green-500' :
                        'bg-slate-400'
                      }`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{entry.subject}</span>
                          <Badge className="bg-slate-100 text-slate-600 text-xs">{entry.direction}</Badge>
                          <Badge className="bg-slate-100 text-slate-600 text-xs">{entry.channel}</Badge>
                        </div>
                        {entry.message_body && (
                          <p className="text-sm text-slate-600 line-clamp-2">{entry.message_body}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                          <span>{entry.from_name}</span>
                          <span>{new Date(entry.created_at).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'linked' && (
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
            <h3 className="text-lg font-barlow font-bold uppercase mb-4">Linked Items</h3>
            
            {(!si.linked_items || si.linked_items.length === 0) ? (
              <p className="text-slate-400 text-center py-8">No linked items</p>
            ) : (
              <div className="space-y-3">
                {si.linked_items.map((item, idx) => (
                  <div
                    key={idx}
                    onClick={() => {
                      if (item.type === 'RFI') {
                        navigate(`/projects/${projectId}/rfis/${item.id}`);
                      } else if (item.type === 'Notice') {
                        navigate(`/projects/${projectId}/events/${item.id}`);
                      }
                    }}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-sm cursor-pointer hover:bg-slate-100"
                  >
                    <div className="flex items-center gap-3">
                      {item.type === 'RFI' && <FileQuestion className="h-5 w-5 text-cyan-500" />}
                      {item.type === 'Notice' && <AlertCircle className="h-5 w-5 text-orange-500" />}
                      <div>
                        <p className="font-medium">{item.type}</p>
                        {item.number && <p className="text-sm text-slate-500">{item.number}</p>}
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-400" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
